import { Box } from "@mui/material";
import { type KeyboardEvent, type ReactNode, useRef } from "react";

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional per-option `view-transition-name` (used by the schedule view-mode toggle). */
  viewTransitionName?: string;
}

interface SegmentedControlProps<T extends string> {
  ariaLabel: string;
  options: ReadonlyArray<SegmentedControlOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Fixed control width. Defaults to sizing to content (equal-width segments). */
  width?: number | string;
  /** Label casing. Defaults to `capitalize`; pass `none` for multi-word labels. */
  labelTextTransform?: "none" | "capitalize";
  /** Optional `view-transition-name` for the sliding thumb (used by the schedule toggle). */
  thumbViewTransitionName?: string;
  /**
   * Called on hover/focus/pointer-down intent for a not-yet-selected option, before any
   * click. The schedule toggle uses this to preload the target view.
   */
  onOptionIntent?: (value: T) => void;
}

/**
 * Segmented control with a sliding active thumb — extracted from the schedule tab's
 * Day/Month view-mode selector so it can be shared (e.g. the Workplace Analytics "Group
 * by" filter). The schedule-specific animation (`view-transition-name`s) and preload
 * intent are wired through optional props; everyday callers just pass options/value/onChange.
 */
export function SegmentedControl<T extends string>(props: SegmentedControlProps<T>) {
  const {
    ariaLabel,
    options,
    value,
    onChange,
    width,
    labelTextTransform = "capitalize",
    thumbViewTransitionName,
    onOptionIntent,
  } = props;

  const selectedIndex = options.findIndex((option) => option.value === value);

  const optionRefs = useRef<Array<HTMLElement | null>>([]);

  function moveSelection(targetIndex: number) {
    const targetOption = options[targetIndex];
    if (!targetOption || targetOption.value === value) {
      return;
    }

    onOptionIntent?.(targetOption.value);
    onChange(targetOption.value);
    optionRefs.current[targetIndex]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>, index: number) {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        moveSelection((index + 1) % options.length);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        moveSelection((index - 1 + options.length) % options.length);
        break;
      case "Home":
        event.preventDefault();
        moveSelection(0);
        break;
      case "End":
        event.preventDefault();
        moveSelection(options.length - 1);
        break;
      default:
        break;
    }
  }

  return (
    <Box
      aria-label={ariaLabel}
      role="radiogroup"
      sx={(theme) => ({
        position: "relative",
        isolation: "isolate",
        display: "grid",
        gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
        alignItems: "center",
        backgroundColor: theme.palette.background?.inactive ?? theme.palette.action.hover,
        borderRadius: theme.borderRadius?.xLarge,
        overflow: "clip",
        width,
        height: theme.size?.button.medium.height,
      })}
    >
      <Box
        aria-hidden
        sx={(theme) => ({
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: `calc(100% / ${options.length})`,
          borderRadius: theme.borderRadius?.xLarge,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `inset 0 0 0 2px ${theme.palette.border?.inactive ?? theme.palette.divider}`,
          transform: `translateX(calc(${Math.max(selectedIndex, 0)} * 100%))`,
          transition: theme.transitions.create(["transform", "box-shadow"], {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeInOut,
          }),
          viewTransitionName: thumbViewTransitionName,
          willChange: "transform",
          opacity: selectedIndex >= 0 ? 1 : 0,
        })}
      />
      {options.map((option, index) => {
        const isSelected = option.value === value;
        // Roving tabindex: only the selected option is tabbable (or the first, when nothing
        // is selected), so the group occupies a single stop in the tab sequence.
        const isTabbable = selectedIndex >= 0 ? isSelected : index === 0;
        const handleIntent = () => {
          if (!isSelected) {
            onOptionIntent?.(option.value);
          }
        };

        return (
          <Box
            key={option.value}
            ref={(node: HTMLElement | null) => {
              optionRefs.current[index] = node;
            }}
            aria-checked={isSelected}
            component="button"
            role="radio"
            tabIndex={isTabbable ? 0 : -1}
            sx={(theme) => ({
              position: "relative",
              zIndex: 1,
              backgroundColor: "transparent",
              border: "none",
              borderRadius: theme.borderRadius?.xLarge,
              color: isSelected ? theme.palette.text.primary : theme.palette.text.secondary,
              cursor: isSelected ? "default" : "pointer",
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.body2.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              flex: 1,
              height: "100%",
              paddingX: 3,
              paddingY: 1,
              whiteSpace: "nowrap",
              textTransform: labelTextTransform,
              transition: theme.transitions.create(["color"], {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeInOut,
              }),
              viewTransitionName: option.viewTransitionName,
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -2,
              },
            })}
            type="button"
            onFocus={handleIntent}
            onKeyDown={(event) => {
              handleKeyDown(event, index);
            }}
            onMouseEnter={handleIntent}
            onPointerDown={handleIntent}
            onClick={() => {
              if (!isSelected) {
                handleIntent();
                onChange(option.value);
              }
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}
